import { useIntl } from 'react-intl';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { CurrencyCode } from '@99designs/i18n';
import { toUnit } from '@99designs/marketing-ui';
import { getSavingsAmountInCents } from '../lib/productSavingsAmounts';
import { getRelatedProductsByKey } from '../lib/relatedProducts';
import type { MarketingKey } from '@99designs/marketing-ui';
import type { RelatedProductNames } from '../lib/relatedProducts';
import type { RelatedProduct } from '../types';

export function useRelatedProductsConfig(
    relatedProductsFromGraph: RelatedProduct[] | null,
    currency: CurrencyCode
) {
    const intl = useIntl();

    if (!relatedProductsFromGraph) return [];

    const roundAmountInCentsToWholeAmount = (amountInCents: number, currency: CurrencyCode) => {
        return Math.round(toUnit(amountInCents, currency));
    };

    const getFormattedPrice = (amount: number, currency: string) => {
        return intl.formatNumber(amount, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
        });
    };

    return relatedProductsFromGraph.map((product: RelatedProduct) => {
        const productFromConfig = getRelatedProductsByKey(product.name as RelatedProductNames);

        const savingsAmount = product.marketingKey
            ? getSavingsAmountInCents(product.marketingKey as MarketingKey, currency)
            : undefined;

        let saving = undefined;

        if (productFromConfig.saving) {
            saving = productFromConfig.saving;
        } else if (savingsAmount) {
            saving = formatMessage(__('Save {price}'), {
                price:
                    typeof savingsAmount === 'number'
                        ? getFormattedPrice(
                              roundAmountInCentsToWholeAmount(savingsAmount, currency),
                              currency
                          )
                        : savingsAmount,
            });
        }

        return {
            ...product,
            title: productFromConfig.title,
            description: productFromConfig.description,
            saving,
            url: productFromConfig.url,
            image: productFromConfig.image,
        };
    });
}
